import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import NotesIcon from '@mui/icons-material/StickyNote2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GeocodeResult, PlannerAction, Waypoint } from '../types';
import { formatCoord, parseCoordinateInput } from '../geo/format';
import { searchPlaces } from '../api/routingApi';

const GOLD = '#f5b942';

interface WaypointRowProps {
  waypoint: Waypoint;
  index: number;
  total: number;
  onDelete: () => void;
  onFocus: () => void;
  onEditNotes: () => void;
}

const WaypointRow: React.FC<WaypointRowProps> = ({ waypoint, index, total, onDelete, onFocus, onEditNotes }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: waypoint.id });
  const isOrigin = index === 0;
  const isDestination = index === total - 1 && total > 1;
  const letter = String(index + 1);
  const color = isOrigin ? '#2e9e5b' : isDestination ? '#e0563d' : GOLD;

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, py: 0.35,
        opacity: isDragging ? 0.6 : 1,
        '&:hover .wp-actions': { opacity: 1 },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Reorder waypoint ${letter}`}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.disabled' }}
      >
        <DragIndicatorIcon sx={{ fontSize: 15 }} />
      </Box>
      <Box sx={{
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: color, color: '#0b1017', fontSize: '0.6rem', fontWeight: 800,
      }}>
        {letter}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onFocus}>
        <Typography variant="caption" noWrap sx={{ display: 'block', lineHeight: 1.3 }}>
          {waypoint.label
            || (waypoint.labelStatus === 'loading' ? 'Looking up…' : formatCoord(waypoint.longitude, waypoint.latitude))}
        </Typography>
        {waypoint.label && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.62rem' }}>
            {formatCoord(waypoint.longitude, waypoint.latitude)}
          </Typography>
        )}
        {waypoint.notes && (
          <Typography
            variant="caption"
            noWrap
            sx={{ display: 'block', fontSize: '0.62rem', color: GOLD }}
          >
            {waypoint.notes}
          </Typography>
        )}
      </Box>
      <Box
        className="wp-actions"
        sx={{ opacity: waypoint.notes ? 1 : 0, transition: 'opacity 120ms ease' }}
      >
        <Tooltip title={waypoint.notes ? 'Edit note' : 'Add note'}>
          <IconButton
            size="small"
            onClick={onEditNotes}
            aria-label={`${waypoint.notes ? 'Edit' : 'Add'} note for waypoint ${letter}`}
          >
            <NotesIcon sx={{ fontSize: 13, color: waypoint.notes ? GOLD : undefined }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton size="small" onClick={onDelete} aria-label={`Remove waypoint ${letter}`}>
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

interface WaypointListProps {
  waypoints: Waypoint[];
  dispatch: React.Dispatch<PlannerAction>;
  proximity?: { longitude: number; latitude: number } | null;
  onFocusWaypoint?: (waypoint: Waypoint) => void;
  onFlyTo?: (longitude: number, latitude: number) => void;
}

const locateErrorMessage = (code?: number): string => {
  if (code === 1) return 'Location permission was denied.';
  if (code === 2) return 'Location is unavailable.';
  if (code === 3) return 'Location request timed out.';
  return 'Could not find your location.';
};

const WaypointList: React.FC<WaypointListProps> = ({
  waypoints,
  dispatch,
  proximity,
  onFocusWaypoint,
  onFlyTo,
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [operatorFix, setOperatorFix] = useState<{ longitude: number; latitude: number } | null>(null);
  const [noteFor, setNoteFor] = useState<Waypoint | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const searchBias = proximity || operatorFix;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => waypoints.map((w) => w.id), [waypoints]);

  useEffect(() => {
    const typed = query.trim();
    if (typed.length < 3) {
      setOptions([]);
      return undefined;
    }
    if (parseCoordinateInput(typed)) {
      setOptions([]);
      return undefined;
    }
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        setOptions(await searchPlaces(typed, searchBias, controller.signal));
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, searchBias]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    dispatch({
      type: 'REORDER_WAYPOINTS',
      fromIndex: ids.indexOf(String(active.id)),
      toIndex: ids.indexOf(String(over.id)),
    });
  };

  const addAt = (longitude: number, latitude: number, label?: string, address?: string) => {
    dispatch({ type: 'ADD_WAYPOINT', longitude, latitude, label, address, source: 'search' });
    onFlyTo?.(longitude, latitude);
  };

  const commitTyped = () => {
    const parsed = parseCoordinateInput(query);
    if (!parsed) return;
    addAt(parsed.longitude, parsed.latitude);
    setQuery('');
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setLocateError('Location is not available in this browser.');
      return;
    }
    setLocateError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const longitude = pos.coords.longitude;
        const latitude = pos.coords.latitude;
        setLocating(false);
        setOperatorFix({ longitude, latitude });
        addAt(longitude, latitude);
      },
      (err) => {
        setLocating(false);
        setLocateError(locateErrorMessage(err.code));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 },
    );
  };

  return (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {waypoints.map((wp, index) => (
            <WaypointRow
              key={wp.id}
              waypoint={wp}
              index={index}
              total={waypoints.length}
              onDelete={() => dispatch({ type: 'DELETE_WAYPOINT', id: wp.id })}
              onFocus={() => onFocusWaypoint?.(wp)}
              onEditNotes={() => { setNoteFor(wp); setNoteDraft(wp.notes || ''); }}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75 }}>
        <Autocomplete
          freeSolo
          size="small"
          options={options}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.address || o.label)}
          inputValue={query}
          onInputChange={(_, value, reason) => { if (reason !== 'reset') setQuery(value); }}
          onChange={(_, value) => {
            if (!value || typeof value === 'string') return;
            addAt(value.longitude, value.latitude, value.label, value.address);
            setQuery('');
            setOptions([]);
          }}
          renderOption={(props, option) => (
            <li {...props} key={`${option.longitude},${option.latitude}`}>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{option.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                  {option.address}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={waypoints.length === 0 ? 'Search an address or place' : 'Add a stop, address, or lat, lon'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && parseCoordinateInput(query)) {
                  e.preventDefault();
                  commitTyped();
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.25, mr: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {searching && <CircularProgress size={12} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Search an address or place',
              }}
            />
          )}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem',
              bgcolor: 'action.hover',
            },
            '& .MuiOutlinedInput-input': { py: 0.85 },
          }}
        />
        <Tooltip title="Use my location as a stop">
          <span>
            <IconButton
              size="small"
              aria-label="Use my location as a stop"
              onClick={handleLocate}
              disabled={locating}
              sx={{
                mt: 0.15,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                color: GOLD,
              }}
            >
              {locating ? <CircularProgress size={14} /> : <MyLocationIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {locateError && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#e0563d' }}>
          {locateError}
        </Typography>
      )}
      {parseCoordinateInput(query) && (
        <Typography
          variant="caption"
          onClick={commitTyped}
          sx={{ display: 'block', mt: 0.5, color: GOLD, fontWeight: 700, cursor: 'pointer' }}
        >
          Add {formatCoord(parseCoordinateInput(query)!.longitude, parseCoordinateInput(query)!.latitude)}
        </Typography>
      )}

      <Dialog open={!!noteFor} onClose={() => setNoteFor(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
            Note for {noteFor?.label || (noteFor ? formatCoord(noteFor.longitude, noteFor.latitude) : '')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            What the team should do at this stop. Saved with the mission.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="Park north of the gate, photograph the loading dock…"
          />
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, justifyContent: 'flex-end' }}>
            <Typography
              variant="caption"
              onClick={() => setNoteFor(null)}
              sx={{ fontWeight: 700, cursor: 'pointer' }}
            >
              Cancel
            </Typography>
            <Typography
              variant="caption"
              onClick={() => {
                if (noteFor) dispatch({ type: 'SET_WAYPOINT_NOTES', id: noteFor.id, notes: noteDraft });
                setNoteFor(null);
              }}
              sx={{ fontWeight: 700, cursor: 'pointer', color: GOLD }}
            >
              Save note
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WaypointList;
